/**
 * @file src/components/AppErrorFallback/AppErrorFallback.tsx
 * @exports AppErrorFallback, AUTO_REFRESH_COOLDOWN_MS, AUTO_REFRESH_DELAY_MS, AUTO_REFRESH_STORAGE_KEY, isModuleLoadError
 * @summary Full-page fallback for a route that could not render: names the problem, offers a refresh, and
 *          refreshes once by itself when the cause is a build chunk the server no longer has.
 * @remarks A deploy replaces every hashed file under `/assets/`, so a tab opened on the previous build
 *          fails the next route it lazy-loads (FRN-3612). TanStack's `lazyRouteComponent` already reloads
 *          once, immediately; during a rolling deploy that reload can still land on the old HTML, which is
 *          what this page then catches. The delayed refresh here gives the rollout time to finish. Both
 *          reloads are guarded by sessionStorage flags, so the worst case is this page with a button, never
 *          a loop. This page's guard expires after `AUTO_REFRESH_COOLDOWN_MS`, so a tab left open across
 *          several deploys refreshes itself on each one. Styled as the vID outage screens (ServiceUnavailable),
 *          but it also stands in for Sentry's boundary, outside every provider: it brings its own theme
 *          and shows only the bare vID header bar, since the full header reads the vID session.
 */

import {
	cerebellumTheme,
	colors,
	highContrastTheme,
	PrimaryButton,
	TextButton,
	ThemeProvider,
	VID,
	Warning,
} from "@cerebruminc/cerebellum";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";

import { VidLogo } from "src/components/Icons";
import { AUTH_PAGE_PATHS, paths } from "src/const";
import { ANALYTICS, analytics, type AnalyticsUserErrorReason } from "src/helpers";
import { reloadDocument } from "src/helpers/browser/reloadDocument";
import { isEmbedded } from "src/helpers/utils/isEmbedded";
import { highContrastThemeAtom } from "src/shame/shame-atoms";
import { HeaderBase, LogoBox } from "src/views/VID/components/VidHeader";
import {
	CenterBoxWrapper,
	MessageActions,
	MessageBodyText,
	MessageCenterBox,
	MessageContainer,
	MessageHeader,
	MessageIconBox,
	MessageIconWrapper,
	SkinnyIconBadgeOverlay,
	VidCenterBase,
} from "src/views/VID/views/VidStyles";

import styles from "./AppErrorFallback.module.css";

/** Long enough for a rolling deploy to be serving the new build everywhere. */
export const AUTO_REFRESH_DELAY_MS = 10_000;
/** Holds the time of the last automatic refresh, so a failure soon after it stops at the button. */
export const AUTO_REFRESH_STORAGE_KEY = "cognition_route_auto_refresh";
/** A failure this long after the last automatic refresh is a new deploy, not the same one failing again. */
export const AUTO_REFRESH_COOLDOWN_MS = 5 * 60_000;

const contactSupport = (): void => {
	window.location.href = "mailto:support@cerebrum.com";
};

// Chromium, Safari and Firefox each word the failed dynamic import differently; webpack's wording is
// kept so a bundler change does not silently turn these back into generic errors.
const MODULE_LOAD_PATTERNS = [
	/Failed to fetch dynamically imported module/i,
	/Importing a module script failed/i,
	/error loading dynamically imported module/i,
	/Loading (?:CSS )?chunk [\w-]+ failed/i,
];

/** Whether an error is the browser failing to fetch a build chunk, as opposed to a render error. */
export const isModuleLoadError = (error: unknown): boolean => {
	const message = error instanceof Error ? error.message : typeof error === "string" ? error : "";
	return MODULE_LOAD_PATTERNS.some((pattern) => pattern.test(message));
};

const isVidPath = (pathname: string): boolean => pathname.startsWith("/vid/") || pathname === paths.vidRemoteIdUpload;

// Mirrors App: the high-contrast theme applies only on vID and authentication pages.
const isHighContrastPath = (pathname: string): boolean =>
	isVidPath(pathname) || AUTH_PAGE_PATHS.has(pathname) || pathname.startsWith("/auth/");

const hasAutoRefreshed = (): boolean => {
	try {
		const refreshedAt = Number(sessionStorage.getItem(AUTO_REFRESH_STORAGE_KEY));
		return Date.now() - refreshedAt < AUTO_REFRESH_COOLDOWN_MS;
	} catch {
		return true;
	}
};

const markAutoRefreshed = (): void => {
	try {
		sessionStorage.setItem(AUTO_REFRESH_STORAGE_KEY, String(Date.now()));
	} catch {
		// Storage is unavailable; the refresh still runs, and hasAutoRefreshed() reported true to stop a repeat.
	}
};

interface AppErrorFallbackProps {
	error: unknown;
	/** Re-renders the failed subtree; offered for render errors, where a retry can succeed without a reload. */
	reset?: () => void;
}

/** The page shown in place of a route that threw; see the file header for when it refreshes by itself. */
export const AppErrorFallback = ({ error, reset }: AppErrorFallbackProps) => {
	const moduleLoadError = isModuleLoadError(error);
	const [autoRefreshPending] = useState(() => moduleLoadError && !hasAutoRefreshed());

	useEffect(() => {
		const reason: AnalyticsUserErrorReason = moduleLoadError ? "route_load_failed" : "page_render_failed";
		analytics.track(ANALYTICS.userErrorShown, { reason });
	}, [moduleLoadError]);

	useEffect(() => {
		if (!autoRefreshPending) {
			return;
		}
		markAutoRefreshed();
		const timer = window.setTimeout(reloadDocument, AUTO_REFRESH_DELAY_MS);
		return () => window.clearTimeout(timer);
	}, [autoRefreshPending]);

	const { pathname } = window.location;
	const highContrast = useAtomValue(highContrastThemeAtom) && isHighContrastPath(pathname);
	const showHeader = isVidPath(pathname) && !isEmbedded();

	return (
		<ThemeProvider theme={highContrast ? highContrastTheme : cerebellumTheme}>
			<main className={styles.page}>
				{showHeader ? (
					<HeaderBase>
						<LogoBox>
							<VidLogo highContrast={highContrast} />
						</LogoBox>
					</HeaderBase>
				) : null}
				<VidCenterBase>
					<CenterBoxWrapper>
						<MessageCenterBox>
							<section aria-labelledby="app-error-heading" role="alert">
								<MessageContainer>
									<MessageIconWrapper>
										<MessageIconBox $highContrast={highContrast}>
											<VID fill={colors.WHITE} />
										</MessageIconBox>
										<SkinnyIconBadgeOverlay>
											<Warning fill={colors.RED_100} />
										</SkinnyIconBadgeOverlay>
									</MessageIconWrapper>
									<MessageHeader id="app-error-heading">
										{moduleLoadError ? "This page didn’t load" : "Something went wrong"}
									</MessageHeader>
									<MessageBodyText>
										{moduleLoadError
											? "This site may have just been updated, or the connection dropped while it was loading. Refresh to pick up where you left off."
											: "Refresh the page to try again."}
									</MessageBodyText>
									{autoRefreshPending ? (
										<MessageBodyText>Refreshing automatically in a few seconds…</MessageBodyText>
									) : null}
									<MessageActions>
										<PrimaryButton onClick={reloadDocument} text="Refresh" />
										{reset && !moduleLoadError ? <TextButton onClick={reset} text="Try again" /> : null}
										<TextButton onClick={contactSupport} text="Contact support" />
									</MessageActions>
								</MessageContainer>
							</section>
						</MessageCenterBox>
					</CenterBoxWrapper>
				</VidCenterBase>
			</main>
		</ThemeProvider>
	);
};
